import { Fragment } from 'react';
import { Dialog, DialogPanel, Transition, TransitionChild } from '@headlessui/react';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_CURRENCIES } from '@sentola/money';
import type { SupportedCurrency } from '@sentola/money';
import IconX from './Icon/IconX';

export type UnsupportedCurrencyDialogProps = {
    isOpen: boolean;
    attemptedCode: string;
    onSelect: (code: SupportedCurrency) => void;
    onClose: () => void;
};

/** Blocks an off-allowlist currency pick and requires a Supported Currency instead. */
export default function UnsupportedCurrencyDialog({ isOpen, attemptedCode, onSelect, onClose }: UnsupportedCurrencyDialogProps) {
    const { t } = useTranslation();

    return (
        <Transition appear show={isOpen} as={Fragment}>
            <Dialog as="div" open={isOpen} onClose={onClose} className="relative z-50">
                <TransitionChild as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
                    <div className="fixed inset-0 bg-[black]/70 backdrop-blur-sm" />
                </TransitionChild>
                <div className="fixed inset-0 z-[999] overflow-y-auto">
                    <div className="flex items-center justify-center min-h-screen px-4 py-8">
                        <TransitionChild
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <DialogPanel className="panel border-0 p-0 rounded-[32px] overflow-hidden w-full max-w-md text-black dark:text-white-dark shadow-2xl bg-white/95 backdrop-blur-md dark:bg-[#121c2c]/95">
                                <div className="text-xl font-bold bg-white/50 dark:bg-[#121c2c]/50 pl-8 pr-8 py-6 flex justify-between items-center">
                                    <span>{t('unsupported_currency.title')}</span>
                                    <button
                                        onClick={onClose}
                                        type="button"
                                        className="text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800"
                                    >
                                        <IconX className="w-6 h-6" />
                                    </button>
                                </div>

                                <div className="p-8">
                                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                                        {t('unsupported_currency.description', { code: attemptedCode })}
                                    </p>
                                    <div className="grid grid-cols-3 gap-3">
                                        {SUPPORTED_CURRENCIES.map((code) => (
                                            <button
                                                key={code}
                                                type="button"
                                                className="btn btn-outline-primary rounded-full font-bold border-2 hover:bg-primary hover:text-white transition-all"
                                                onClick={() => onSelect(code)}
                                            >
                                                {code}
                                            </button>
                                        ))}
                                    </div>
                                    <div className="flex justify-center items-center mt-8 pt-6 border-t border-gray-100 dark:border-gray-700/50">
                                        <button
                                            onClick={onClose}
                                            type="button"
                                            className="btn btn-outline-secondary px-6 h-12 rounded-full font-bold border-2 transition-all"
                                        >
                                            {t('cancel')}
                                        </button>
                                    </div>
                                </div>
                            </DialogPanel>
                        </TransitionChild>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
}
